import React, { useState } from 'react'
import {
  Box,
  Button,
  Input,
  VStack,
  Text,
  Heading,
  Field,
  Alert,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { AuthService } from '../../services/authService'
import { Logo } from '../Brand/Logo'

interface ForgotPasswordFormProps {
  onBackToLogin?: () => void
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onBackToLogin,
}) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      
      // // Check if user exists first
      // const userExists = await AuthService.checkUserExists(email)
      // if (!userExists) {
      //   setError(t('auth.no_account_found'))
      //   return
      // }
      
      await AuthService.sendPasswordReset(email)
      console.log('✅ Password reset request completed successfully')
      setIsSubmitted(true)
    } catch (err) {
      console.error('❌ Password reset failed:', err)
      const errorMessage = err instanceof Error ? err.message : t('auth.reset_email_failed')
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    if (onBackToLogin) {
      onBackToLogin()
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    // Clear error when user starts typing
    if (error) {
      setError('')
    }
  }

  if (isSubmitted) {
    return (
      <Box
        w="full"
        maxW="md"
        mx="auto"
        p={8}
        bg="bg"
        borderRadius="lg"
        shadow="md"
        border="1px"
        borderColor="border"
      >
        <VStack gap={6} align="stretch">
          <VStack gap={4}>
            <Logo size="2xl" />
            <VStack gap={2}>
              <Heading as="h2" size="md" color="green.600" _dark={{ color: 'green.400' }} textAlign="center">
                {t('auth.check_email_title')}
              </Heading>
              <Text color="fg.muted" textAlign="center">
                {t('auth.check_email_subtitle', { email })}
              </Text>
            </VStack>
          </VStack>
          
          <Alert.Root status="success" borderRadius="md">
            <Alert.Indicator />
            <Alert.Content>
              {t('auth.didnt_receive_email')}
            </Alert.Content>
          </Alert.Root>
          
          <VStack gap={3}>
            <Button
              colorScheme="green"
              size="lg"
              w="full"
              onClick={() => {
                setIsSubmitted(false)
                setEmail('')
                setError('')
              }}
            >
              {t('auth.send_another_email')}
            </Button>
            
            {onBackToLogin && (
              <Button
                variant="ghost"
                size="lg"
                w="full"
                onClick={handleBackToLogin}
              >
                {t('auth.back_to_login')}
              </Button>
            )}
          </VStack>
        </VStack>
      </Box>
    )
  }

  return (
    <Box
      w="full"
      maxW="md"
      mx="auto"
      p={8}
      bg="bg"
      borderRadius="lg"
      shadow="md"
      border="1px"
      borderColor="border"
    >
      <VStack gap={6} align="stretch">
        <VStack gap={4}>
          <Logo size="2xl" />
          <VStack gap={2}>
            <Heading as="h2" size="md" color="green.600" _dark={{ color: 'green.400' }} textAlign="center">
              {t('auth.forgot_password_title')}
            </Heading>
            <Text color="fg.muted" textAlign="center">
              {t('auth.forgot_password_subtitle')}
            </Text>
          </VStack>
        </VStack>

        {error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            <Alert.Content>
              {error}
            </Alert.Content>
          </Alert.Root>
        )}

        <Box as="form" onSubmit={handleSubmit}>
          <VStack gap={4}>
            <Field.Root invalid={!!error} w="full">
              <Field.Label fontSize="sm" fontWeight="500" color="fg" mb={2}>
                {t('common.email')}
              </Field.Label>
              <Input
                id="email-input"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder={t('auth.enter_email')}
                bg="bg"
                color="fg"
                borderColor={error ? 'red.500' : 'border'}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
                autoComplete="email"
                required
                disabled={isLoading}
                aria-describedby={error ? 'email-error' : undefined}
                aria-label="Email address"
              />
              {error && <Field.ErrorText>{error}</Field.ErrorText>}
            </Field.Root>

            <Button
              type="submit"
              colorScheme="green"
              size="lg"
              w="full"
              loading={isLoading}
              disabled={!email.trim() || isLoading}
            >
              {t('auth.send_reset_email')}
            </Button>

            {onBackToLogin && (
              <Button
                variant="ghost"
                size="lg"
                w="full"
                onClick={handleBackToLogin}
                disabled={isLoading}
              >
                {t('auth.back_to_login')}
              </Button>
            )}
          </VStack>
        </Box>
      </VStack>
    </Box>
  )
}

export default ForgotPasswordForm 